import type { KanbanStatus } from '../types/kanban.js';

/**
 * ENG-5730 — the single source of truth for what a kanban status transition
 * *means*, replacing the rules that were scattered inline across the
 * `POST /host/kanban` handler in `packages/api/src/routes/host-runtime.ts`.
 *
 * `transition()` is intentionally **pure**: no DB, no I/O. The API layer reads
 * the current status, calls `transition()` to validate + classify the move,
 * applies the row write it already performs today, and (on a real status
 * change) appends a `kanban_events` row. Keeping the policy here makes it unit
 * testable and lets future writers (reaper, console PATCH) adopt the same
 * contract.
 *
 * Design (see the ENG-5730 plan review): the table is deliberately PERMISSIVE,
 * mirroring today's behaviour where the handler accepts any valid status → any
 * valid status. We reject only the two moves that are unambiguously wrong:
 *   1. an unknown status string, and
 *   2. "resurrecting" a closed card — `done | failed | cancelled` back to an
 *      active state (`backlog | todo | in_progress`).
 * Everything else (direct jumps like `backlog → done`, terminal reshuffles like
 * `done → failed`) stays allowed so existing API contracts don't change.
 */

/** The full status set, aligned with the agent_kanban_items DB CHECK. */
export const KANBAN_STATUSES = [
  'backlog',
  'todo',
  'in_progress',
  'done',
  'failed',
  'cancelled',
  'needs_attention',
] as const;

/** Active (open) states a card can be worked from. */
export const KANBAN_ACTIVE_STATES: ReadonlySet<KanbanStatus> = new Set<KanbanStatus>([
  'backlog',
  'todo',
  'in_progress',
]);

/**
 * Closed states that must not be resurrected back to an active state by an
 * agent write. `needs_attention` is intentionally NOT here: although the reaper
 * treats it as terminal, an operator/user (and the agent itself, once the issue
 * is addressed) can legitimately revive it. It is therefore a normal active-ish
 * state from the state machine's perspective and never blocks a move.
 */
export const KANBAN_RESURRECTION_BLOCKED: ReadonlySet<KanbanStatus> = new Set<KanbanStatus>([
  'done',
  'failed',
  'cancelled',
]);

const KANBAN_STATUS_SET: ReadonlySet<string> = new Set<string>(KANBAN_STATUSES);

/** Narrowing guard for an arbitrary string against the canonical status set. */
export function isKanbanStatus(value: unknown): value is KanbanStatus {
  return typeof value === 'string' && KANBAN_STATUS_SET.has(value);
}

export type TransitionFailureCode = 'unknown_status' | 'invalid_transition';

/**
 * Result of {@link transition}. `changed` distinguishes a real status move
 * (`from !== to`) from an idempotent re-write (`from === to`). Callers append a
 * `kanban_events` row only when `ok && changed` — a `done → done` re-issue or a
 * notes/progress-only update must NOT produce an event (it would flood the
 * append-only ledger with no-signal heartbeats).
 */
export type TransitionResult =
  | { ok: true; from: KanbanStatus | null; to: KanbanStatus; changed: boolean }
  | {
      ok: false;
      code: TransitionFailureCode;
      from: KanbanStatus | null;
      attempted: string;
    };

/**
 * Validate and classify a kanban status transition.
 *
 * @param from the card's current status, or `null` for a brand-new card (the
 *   add path) — a null `from` permits any valid initial status.
 * @param to   the requested next status (raw string; validated here).
 */
export function transition(from: KanbanStatus | null, to: string): TransitionResult {
  if (!isKanbanStatus(to)) {
    return { ok: false, code: 'unknown_status', from, attempted: to };
  }

  // Add path: a new card may start in any valid status (parity with today's
  // permissive add, which accepts e.g. an item created directly as `done`).
  if (from === null) {
    return { ok: true, from: null, to, changed: true };
  }

  // Idempotent re-write — allowed, but flagged as no-change so the caller skips
  // the event write. Covers `done → done` completion re-issues that the
  // confirmation idempotency gate depends on succeeding.
  if (from === to) {
    return { ok: true, from, to, changed: false };
  }

  // The one genuinely-invalid move: resurrecting a closed card back to active
  // work. Terminal reshuffles (e.g. `done → failed`) stay allowed.
  if (KANBAN_RESURRECTION_BLOCKED.has(from) && KANBAN_ACTIVE_STATES.has(to)) {
    return { ok: false, code: 'invalid_transition', from, attempted: to };
  }

  return { ok: true, from, to, changed: true };
}
